import DwinityLoader from "../components/DwinityLoader";
import React, { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from 'react-i18next';
import FileUploadComponent from "../components/FileUploadComponent";
import { AppContext } from "../AppContextProvider";
import { WalletProviderContext } from "../walletprovider/WalletProvider";
import { writeFile, fileExists, FileExistsError, FileWriteError } from "../utils/dwclient/dwstorage";
import {
  mkdirs2,
  firstLink2 as firstLink,
  newFile2 as newFile,
  getAllFoldersWithIds2 as getAllFoldersWithIds,
  addProperties2,
} from "../utils/graph/graphmodifier2";
import { unProcessAndSaveProcessedGraphJwt, loadGraphWithJwt } from "../utils/graph/graphloader2";
import { loadJwt } from "../utils/dwclient/localstorage/jwtStorage";
import { WarningTicket, InformationsTicket, NotificationsTicket, ErrorTicket } from "../components/TicketsComponent";
import { addLeadingSlash, splitPath, makeFolderName, canonicalFolderPath, formatTimeHHMMSS } from "../utils/paths";

import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalFooter,
  MDBModalBody,
  MDBModalHeader,
  MDBSelect,
  MDBInput,
  MDBBtn,
} from "mdb-react-ui-kit";
import { DwMpcWalletBackendProviderContext } from "../mpcwalletprovider/DwMpcWalletBackendProvider";
import { isZip, isZipFileContent, unzip } from "../utils/zip/unzipper";
import { addFile, removeAll, initUploadWorker, interrupt, setPlannedUploadPaths, setPlannedUploadPathsAsync, clearUploadSession } from "../utils/uploadworker/uploadworkerutil";
import { findFileSystemRoot2 } from "../utils/graph/graphstorage2";
import { checkOrchestratorHealth } from "../utils/uploadworker/orchestratorClient";
import { startOrchestratedUpload } from "../utils/uploadworker/orchestratedUpload";

export default function FileUploadsModal(props) {
  const { t } = useTranslation();
  const basicRef = useRef(null);
  const { selectedAccount, selectedWallet } = useContext(WalletProviderContext);
  const { storageProps } = useContext(DwMpcWalletBackendProviderContext);
  const context = useContext(AppContext);
  const [isFiles, setIsFiles] = useState(false);
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState("");
  const [selectedPath, setSelectedPath] = useState();
  const [newPath, setNewPath] = useState("");
  const [folderList, setFolderList] = useState([]);
  const [isVisibility5, setIsVisibility5] = useState(false);
  const [processedGraph, setProcessedGraph] = useState(undefined);
  const processedGraphRef = useRef(undefined);
  const [isErrorTicket, setIsErrorTicket] = useState(false);
  const [errortext, setErrortext] = useState("");
  const [isPreparing, setIsPreparing] = useState(false);
  const [bar, setBar] = useState(document.getElementById("progress-bar"));
  const [size, setSize] = useState(document.getElementById("file-size"));
  const [details, setDetails] = useState(document.getElementById("file-details"));
  const [timer, setTimer] = useState(document.getElementById("file-timer"));
  const [timertext, setTimertext] = useState(document.getElementById("file-timer-text"));
  const [filename, setFilename] = useState(document.getElementById("file-name"));
  const [filenumber, setFilenumber] = useState(document.getElementById("file-number"));
  const [resumeBatchInfo, setResumeBatchInfo] = useState(null);
  const [resumeWarning, setResumeWarning] = useState("");
  const actuelleFile = { actuelleFile: null };
  const maxUploadSize = process.env.MAX_UPLOAD_SIZE ?? 536870912;

  // Helper function to process graph nodes (matching ExplorerComponent logic)
  const processGraphNodes = (graph) => {
    for (let node of graph.nodes) {
      if (node.value !== undefined) {
        node.value2 = node.value;
        delete node.value;
      }
      if (node.label !== undefined) {
        node.title = node.label;
      }
      if (node.cid > 9) {
        node.cid = 9;
      }
      if (node.level > 9) {
        node.level = 9;
      }
      if (node.cid === 0 && node.level === 1) {
        node.cid = 9;
      }
      addProperties2(node);
    }
    return graph;
  };

  // Initialize folder list from a graph
  const initializeFolderList = (graph) => {
    let folderlistactuell = getAllFoldersWithIds(graph, "/dataOwnerFiles/")
      .filter((k) => !k.path.includes("Imported"))
      .filter((k) => !k.path.includes("dataOwnerFiles"))
      .filter((k) => k.path !== "/")
      .map((k) => {
        return {
          text: k.path.replace("/dataOwnerFiles", ""),
          value: k.id,
        };
      });
    setFolderList(folderlistactuell);
    if (props.modalText !== "") {
      let folder = folderlistactuell.filter((k) => k.value === props.modalText)[0];
      setSelectedPath([folder?.value, folder?.text]);
    } else {
      let folder = folderlistactuell[0];
      setSelectedPath([folder?.value, folder?.text]);
    }
  };

  useEffect(() => {
    async function initializeModal() {
      if (!props.modalParam) return;
      
      const { processedGraph: localProcessedGraph, resumeBatchId, resumeBasePath, preloadedFiles } = props.modalParam;
      
      // If we have a processedGraph from props, use it directly
      if (localProcessedGraph !== undefined) {
        setProcessedGraph(localProcessedGraph);
        processedGraphRef.current = localProcessedGraph;
        initializeFolderList(localProcessedGraph);
      }

      if (preloadedFiles && preloadedFiles.length > 0) {
        const prepared = preloadedFiles.map((f) => Object.assign(f, { preview: URL.createObjectURL(f), isDelete: false }));
        setFiles(prepared);
        setIsFiles(true);
        return;
      }

      if (localProcessedGraph !== undefined && !preloadedFiles) {
        return;
      }
      
      // If we have resumeBatchId but no graph, we need to load the graph
      if (resumeBatchId) {
        console.log("[FileUploadsModal] Resume mode - loading graph from server");
        try {
          const authToken = await loadJwt();
          if (!authToken) {
            console.error("[FileUploadsModal] No auth token available for resume");
            setIsErrorTicket(true);
            setErrortext(t('files.upload.authRequired') || "Authentication required. Please log in again.");
            return;
          }
          
          const graph = await loadGraphWithJwt(authToken);
          if (!graph) {
            console.error("[FileUploadsModal] Failed to load graph for resume");
            setIsErrorTicket(true);
            setErrortext(t('files.upload.graphLoadFailed') || "Failed to load file structure. Please try again.");
            return;
          }
          
          // Process the graph (same as ExplorerComponent does)
          const processedG = processGraphNodes(graph);
          
          setProcessedGraph(processedG);
          processedGraphRef.current = processedG;
          initializeFolderList(processedG);
          
          // Store resume batch info - fetch completedFiles from server if not provided
          const resumeStatus = await import("../utils/uploadworker/orchestratorClient").then(
            m => m.getBatchStatus(resumeBatchId)
          ).catch(() => null);
          
          setResumeBatchInfo({
            batchId: resumeBatchId,
            basePath: resumeBasePath,
            completedFiles: resumeStatus?.completedFiles || 0,
            totalFiles: resumeStatus?.totalFiles || 0
          });
          
          console.log("[FileUploadsModal] Graph loaded successfully for resume");
        } catch (error) {
          console.error("[FileUploadsModal] Error loading graph:", error);
          setIsErrorTicket(true);
          setErrortext(t('files.upload.graphLoadFailed') || "Failed to load file structure. Please try again.");
        }
      }
    }
    
    initializeModal();
  }, []);

  useEffect(() => {
    if (isFiles && context.getState()?.isProgress) {
      setBar(document.getElementById("progress-bar"));
      setSize(document.getElementById("file-size"));
      setDetails(document.getElementById("file-details"));
      setTimer(document.getElementById("file-timer"));
      setTimertext(document.getElementById("file-timer-text"));
      setFilename(document.getElementById("file-name"));
      setFilenumber(document.getElementById("file-number"));
    }
  }, [context.getState()?.isProgress]);

  // Note: uploadFiles() is now called directly from the Upload button onClick
  // The bar state is only used for DOM element references during upload

  function onUploadProgress(progressEvent) {
    if (progressEvent.loaded === undefined || progressEvent.total === undefined) {
      //if (progressEvent.message === "No next file") {
      //  saveProcessedGraph();
      //}
      return;
    }
    /*
    const percentCompletedHeader = (progressEvent.loadedFilesBytes / progressEvent.totalFilesBytes) * 100;
    if (completedBar !== null) {
      completedBar.setAttribute("value", percentCompletedHeader);
      completedBar.previousElementSibling.textContent = `${percentCompletedHeader.toFixed(0)}%`;
    }
*/
    console.log("progressEvent", progressEvent);
    const percentCompleted = (progressEvent.loaded / progressEvent.total) * 100;
    const sizeCompleted = ((progressEvent.loaded / progressEvent.total) * progressEvent.file.file.size) / 1e6;
    if (bar) bar.style.width = `${percentCompleted}%`;
    const barLabel = document.getElementById("progress-bar-label");
    if (barLabel) barLabel.textContent = `${percentCompleted.toFixed(0)}%`;
    filename.textContent = `${progressEvent.file.file.name} `;
    filenumber.textContent = t('files.upload.fileOfTotal', { current: progressEvent.loadedFiles + 1, total: progressEvent.totalFiles });
    size.textContent = `${sizeCompleted.toFixed(2)}MB of ` + `${(progressEvent.file.file.size / 1e6).toFixed(2)}MB`;
    if (progressEvent.estimated !== undefined) {
      timertext.textContent = t('files.upload.timeRemaining');
      timer.textContent = formatTimeHHMMSS(progressEvent.estimated);
    }
    if (percentCompleted === 100) {
      if (barLabel) barLabel.textContent = t('files.upload.uploadComplete');
      details.textContent = t('files.upload.fileProcessing');
    }
  }

  function onCompleted(parentId, completedEvent) {
    const graph = processedGraphRef.current;
    if (!graph) {
      console.warn("[FileUploadsModal] onCompleted: processedGraph not available");
      return;
    }
    const pathParts = splitPath(completedEvent.filePath);
    if (pathParts?.folder !== undefined) {
      const lastFolderId = mkdirs2(graph, parentId, pathParts.folder);

      let oldId = newFile(
        graph,
        completedEvent.file.name,
        completedEvent.file.size,
        completedEvent.file.type,
        completedEvent.filePath,
        Date.now(),
      );
      firstLink(graph, lastFolderId, oldId);
    }
  }

  function onFinished(finishedEvent) {
    saveProcessedGraph();
  }

  const uploadFiles = async () => {
    console.log("[FileUploadsModal] uploadFiles() called, files.length:", files.length);
    if (files.length > 0 && files.length > files.filter((k) => k.isDelete).length) {
      console.log("[FileUploadsModal] Starting upload preparation...");
      setIsPreparing(true);
      
      const initDomElements = () => {
        const barEl = document.getElementById("progress-bar");
        const sizeEl = document.getElementById("file-size");
        const detailsEl = document.getElementById("file-details");
        const timerEl = document.getElementById("file-timer");
        const timertextEl = document.getElementById("file-timer-text");
        const filenameEl = document.getElementById("file-name");
        const filenumberEl = document.getElementById("file-number");
        if (barEl) setBar(barEl);
        if (sizeEl) setSize(sizeEl);
        if (detailsEl) setDetails(detailsEl);
        if (timerEl) setTimer(timerEl);
        if (timertextEl) setTimertext(timertextEl);
        if (filenameEl) setFilename(filenameEl);
        if (filenumberEl) setFilenumber(filenumberEl);
        return { barEl, sizeEl, filenameEl, filenumberEl, timerEl, timertextEl };
      };
      sessionStorage.removeItem("notDisplayedBuble");
      sessionStorage.removeItem("childrenClose");
      const existingFiles = [];
      const errors = [];
      
      // Delay hideModal until after structure is built
      // props.hideModal(); - moved below after structure preparation

      /**
       * VÁLTOZÁS 2026-01-05 - Követelmény 2.4
       * Folder statisztika inicializálása az upload előtt
       * BroadcastChannel-en keresztül elküldjük az ExplorerComponent-nek
       *
       * A canonicalFolderPath segédfüggvény biztosítja a konzisztens normalizálást
       */
      const activeFiles = files.filter((k) => !k.isDelete);
      
      // Check if resuming upload and file count doesn't match
      if (resumeBatchInfo && resumeBatchInfo.totalFiles !== activeFiles.length) {
        const warningMsg = `Resume upload: Expected ${resumeBatchInfo.totalFiles} files but selected ${activeFiles.length} files. The upload will continue with the selected files.`;
        console.warn("[FileUploadsModal]", warningMsg);
        setResumeWarning(warningMsg);
      }
      const folderStats = new Map();
      const targetPath = selectedPath?.[1] || "/";

      // Update loading indicator - building file structure
      const loadingTextInit = document.getElementById("upload-loading-text");
      if (loadingTextInit) {
        loadingTextInit.textContent = t('files.upload.buildingStructure');
      }

      // CRITICAL: Build complete manifest of ALL file paths BEFORE upload starts
      // Process in chunks to prevent browser freeze with 20k+ files
      const allFilePaths = [];
      const PREP_CHUNK_SIZE = 500;
      
      for (let i = 0; i < activeFiles.length; i += PREP_CHUNK_SIZE) {
        const chunk = activeFiles.slice(i, i + PREP_CHUNK_SIZE);
        
        for (const file of chunk) {
          const fullPath = makeFolderName(targetPath, file.path || file.name);
          allFilePaths.push(fullPath);
          const folderPath = canonicalFolderPath(fullPath, targetPath);
          if (!folderStats.has(folderPath)) {
            folderStats.set(folderPath, { total: 0, completed: 0 });
          }
          folderStats.get(folderPath).total++;
        }
        
        // Yield to browser between chunks
        if (i + PREP_CHUNK_SIZE < activeFiles.length) {
          await new Promise(resolve => setTimeout(resolve, 0));
        }
      }

      // CRITICAL FIX: Set planned paths DIRECTLY - don't rely solely on BroadcastChannel
      // BroadcastChannel may not deliver messages if listener isn't ready
      // Use async version to prevent blocking with large file counts
      console.log(`[FileUploadsModal] Setting planned paths (${allFilePaths.length} files)...`);
      await setPlannedUploadPathsAsync(allFilePaths);
      console.log(`[FileUploadsModal] Planned paths set successfully`);
      
      console.log(`[FileUploadsModal] Sending folder stats via BroadcastChannel...`);

      // Batch file paths to avoid Chrome's structured clone limit (~16MB)
      // With 20000 files, sending all paths at once can exceed this limit
      const BATCH_SIZE = 500;
      if (typeof BroadcastChannel !== "undefined") {
        try {
          const initBroadcast = new BroadcastChannel("upload-channel");
          
          // Send folder stats and metadata first (without the huge file list)
          initBroadcast.postMessage({
            status: "init-folders",
            folderStats: Array.from(folderStats.entries()).map(([path, stats]) => ({
              path,
              totalFiles: stats.total,
              completedFiles: 0,
            })),
            totalFiles: activeFiles.length,
            basePath: targetPath,
            batchedFilePaths: true, // Signal that file paths will be sent in batches
          });
          
          // Send file paths in batches to avoid structured clone limits
          for (let i = 0; i < allFilePaths.length; i += BATCH_SIZE) {
            const batch = allFilePaths.slice(i, i + BATCH_SIZE);
            initBroadcast.postMessage({
              status: "file-paths-batch",
              paths: batch,
              batchIndex: Math.floor(i / BATCH_SIZE),
              totalBatches: Math.ceil(allFilePaths.length / BATCH_SIZE),
              isLastBatch: i + BATCH_SIZE >= allFilePaths.length,
            });
          }
          
          initBroadcast.close();
        } catch (broadcastError) {
          // BroadcastChannel errors should not block the upload
          console.warn("[FileUploadsModal] BroadcastChannel error (non-fatal):", broadcastError);
        }
      }

      // Update loading indicator - building structure complete, now connecting
      console.log("[FileUploadsModal] BroadcastChannel done, updating loading text...");
      const loadingText = document.getElementById("upload-loading-text");
      if (loadingText) {
        loadingText.textContent = t('files.upload.connectingToServer');
      }

      // Check if orchestrator is available for large file uploads (memory-efficient mode).
      // checkOrchestratorHealth() surfaces a degraded (HTTP 503) deploy so the
      // operator sees the config warnings in the browser console without having
      // to curl the server manually.
      console.log("[FileUploadsModal] Checking orchestrator health...");
      const orchestratorHealth = await checkOrchestratorHealth();
      const orchestratorAvailable = orchestratorHealth.available;
      console.log("[FileUploadsModal] Orchestrator available:", orchestratorAvailable, "degraded:", orchestratorHealth.degraded);
      if (orchestratorHealth.degraded && orchestratorHealth.configWarnings?.length) {
        console.warn(
          "[FileUploadsModal] Orchestrator is running but has config warnings — operator action needed:",
          orchestratorHealth.configWarnings
        );
      }
      
      // Ensure processedGraph is available before continuing
      // Use ref instead of state because state update is async
      const graph = processedGraphRef.current;
      if (!graph) {
        console.error("[FileUploadsModal] processedGraph is undefined - cannot proceed with upload");
        setIsErrorTicket(true);
        setErrortext(t('files.upload.graphNotLoaded') || "Graph not loaded. Please try again.");
        setIsPreparing(false);
        return;
      }
      
      const fileSystemRoot = findFileSystemRoot2(graph);
      if (!fileSystemRoot) {
        console.error("[FileUploadsModal] Cannot find file system root in graph");
        setIsErrorTicket(true);
        setErrortext(t('files.upload.noFileSystemRoot') || "Cannot find file system root. Please try again.");
        setIsPreparing(false);
        return;
      }
      const parentId = fileSystemRoot.id;
      
      // Structure is ready, now hide modal and show progress panel
      setIsPreparing(false);
      props.hideModal();
      
      // Now show the progress panel in ExplorerComponent
      context.isProgress(true);
      
      // Wait for progress panel to render, then initialize DOM elements
      await new Promise(resolve => setTimeout(resolve, 150));
      const domElements = initDomElements();
      
      // Hide loading indicator when upload starts
      const loadingIndicator = document.getElementById("upload-loading-indicator");
      if (loadingIndicator) {
        loadingIndicator.style.display = "none";
      }
      
      const resumeBatchId = props.modalParam?.resumeBatchId;
      
      if (orchestratorAvailable) {
        console.log(`[FileUploadsModal] Using orchestrated upload for ${activeFiles.length} files${resumeBatchId ? ` (resuming batch ${resumeBatchId})` : ''}`);
        
        try {
          await startOrchestratedUpload({
            provider: selectedWallet?.provider,
            files: activeFiles,
            basePath: targetPath,
            existingBatchId: resumeBatchId,
            onFileProgress: (data) => {
              const percentCompleted = (data.loaded / data.total) * 100;
              const sizeCompleted = ((data.loaded / data.total) * data.fileSize) / 1e6;
              
              const barEl = domElements.barEl || document.getElementById("progress-bar");
              const barLabelEl = document.getElementById("progress-bar-label");
              const filenameEl = domElements.filenameEl || document.getElementById("file-name");
              const sizeEl = domElements.sizeEl || document.getElementById("file-size");
              
              if (barEl) {
                barEl.style.width = `${percentCompleted}%`;
              }
              if (barLabelEl) {
                barLabelEl.textContent = `${percentCompleted.toFixed(0)}%`;
              }
              if (filenameEl) {
                filenameEl.textContent = `${data.fileName} `;
              }
              if (sizeEl) {
                sizeEl.textContent = `${sizeCompleted.toFixed(2)}MB of ${(data.fileSize / 1e6).toFixed(2)}MB`;
              }
            },
            onBatchProgress: (status) => {
              const filenumberEl = domElements.filenumberEl || document.getElementById("file-number");
              const timerEl = domElements.timerEl || document.getElementById("file-timer");
              const timertextEl = domElements.timertextEl || document.getElementById("file-timer-text");
              
              if (filenumberEl) {
                // WebSocket sends server-side completedFiles which already includes previously uploaded files
                // So we don't need to add resumeOffset - the server tracks the global count
                const displayCurrent = status.completedFiles + 1;
                const displayTotal = status.totalFiles;
                filenumberEl.textContent = t('files.upload.fileOfTotal', { 
                  current: displayCurrent, 
                  total: displayTotal 
                });
              }
              if (timerEl && status.remainingFiles > 0) {
                const estimatedSecondsPerFile = 2;
                const estimatedRemaining = status.remainingFiles * estimatedSecondsPerFile;
                if (timertextEl) timertextEl.textContent = t('files.upload.timeRemaining');
                timerEl.textContent = formatTimeHHMMSS(estimatedRemaining);
              }
            },
            onFileCompleted: (data) => {
              const graph = processedGraphRef.current;
              if (!graph) {
                console.warn("[FileUploadsModal] onFileCompleted: processedGraph not available");
                return;
              }
              const pathParts = splitPath(data.filePath);
              if (pathParts?.folder !== undefined) {
                const lastFolderId = mkdirs2(graph, parentId, pathParts.folder);
                let oldId = newFile(
                  graph,
                  data.fileName,
                  data.fileSize,
                  data.fileType,
                  data.filePath,
                  Date.now(),
                );
                firstLink(graph, lastFolderId, oldId);
              }
            },
            onBatchCompleted: async (status) => {
              console.log(`[FileUploadsModal] Orchestrated upload completed: ${status.completedFiles}/${status.totalFiles} files`);
              await saveProcessedGraph();
            },
            onError: (error, fileIndex, fileName) => {
              console.error(`[FileUploadsModal] File upload error: ${fileName}`, error);
              errors.push(`${fileName}: ${error.message}`);
            },
          });
        } catch (orchestratorError) {
          console.error(`[FileUploadsModal] Orchestrated upload failed, falling back to worker:`, orchestratorError);
          errors.push(`Orchestrator error: ${orchestratorError.message}`);
        }
      } else {
        // Fallback: Send all files to worker queue
        // Memory is now bounded because the worker creates objectURLs only when uploading each file
        // (not upfront for all files like before)
        console.log(`[FileUploadsModal] Using worker-based upload for ${files.length} files`);
        for (let i = 0; i < files.length; i++) {
          try {
            if (!files[i].isDelete) {
              var file = files[i];

              actuelleFile.actuelleFile = files[i];
              delete file.isDelete;
              await uploadSingleFile(file);
            }
          } catch (error) {
            if (error.name === "FileExistsError") {
              existingFiles.push(error.message);
            } else {
              errors.push(error.message);
            }
          }
        }
        console.log(`[FileUploadsModal] All ${files.length} files sent to worker queue`);
      }
      /* try {
        await saveProcessedGraph();
      } catch (error) {
        errors.push("Error writing admin data to graph");
      }*/
      if (existingFiles.length > 0) {
        if (props.modalParam?.onError) {
          props.modalParam.onError("These files exist: " + existingFiles);
        }
        return;
      }
      if (errors.length > 0) {
        console.log("Errors: " + errors);
        if (props.modalParam?.onError) {
          props.modalParam.onError("Errors: " + errors);
        }
        return;
      }
      setFiles([]);
    }
  };

  const saveProcessedGraph = async () => {
    const graph = processedGraphRef.current;
    if (!graph) {
      console.warn("[FileUploadsModal] saveProcessedGraph: processedGraph not available");
      return;
    }
    const authToken = await loadJwt();
    await unProcessAndSaveProcessedGraphJwt(authToken, graph);
    await removeAll();
    clearUploadSession();
    setTimeout(() => {
      //initUploadWorker();
      context.onSetIsRender();
      props.modalParam.onFinish();
    }, 500);
  };

  const uploadSingleFile = async (file) => {
    if (selectedAccount !== null && file !== null && selectedPath?.[1]) {
      /*      let path = "/" + file.path;
      if (selectedPath) {
        path = (selectedPath?.[1] + file.path).replace("/Uploaded", "");
      } else {
        path = file.path;
      }
      let parentId = "";
      let parentTitle = "";
      if (selectedPath) {
        let array = selectedPath[1].split("/");
        parentTitle = array[array.length - 1];
      }

      if (parentTitle !== "" && parentTitle !== "dataOwnerFiles") {
        //parentId = processedGraph.nodes.filter((k) => k.label === parentTitle)[0].id;
        parentId = processedGraph.nodes
          .filter((k) => k.uploadAllowed === true)
          .filter((k) => k.cid === 9)
          .filter((k) => k.layer === "filesystem")
          .filter((k) => k.label === parentTitle)[0].id;
      } else {
        parentId = folderList.filter((k) => k.text === "/Uploaded")[0].value;
      }
      path = "/Uploaded" + addLeadingSlash(path);*/
      let path = makeFolderName(selectedPath[1], file.path);
      
      // Ensure processedGraph is available
      if (!processedGraph) {
        console.error("[FileUploadsModal] processedGraph is undefined in single file upload");
        return;
      }
      const fileSystemRoot = findFileSystemRoot2(processedGraph);
      if (!fileSystemRoot) {
        console.error("[FileUploadsModal] Cannot find file system root in single file upload");
        return;
      }
      const parentId = fileSystemRoot.id;
      /*
      if (await fileExists(storageProps, path)) {
        throw new FileExistsError(path);
      }
        */
      /*if (isZip(file) && !(await isZipFileContent(file))) {
        throw new FileUnzipError(path);
      }*/

      if (file.size > 0) {
        try {
          //console.log("path", path, "File", file);
          //await writeFile(selectedWallet?.provider, storageProps, path, file, onUploadProgress);
          await addFile(
            selectedWallet?.provider,
            file,
            path,
            onUploadProgress,
            (completedEvent) => {
              //console.log("completed", parentId, completedEvent);
              onCompleted(parentId, completedEvent);
            },
            onFinished,
          );
          /*setTimeout(() => {
            interrupt();
          }, 5000);*/
        } catch (error) {
          throw new FileWriteError(path);
        }
      } else {
        path = "";
      }
      /*const isFileExists = await fileExists(storageProps, path);
      if (!isFileExists) {
        const pathParts = splitPath(file.path);
        if (pathParts?.folder !== undefined) {
          const lastFolderId = mkdirs2(processedGraph, parentId, pathParts.folder);

          let oldId = newFile(processedGraph, file.name, file.size, file.type, path, Date.now());
          firstLink(processedGraph, lastFolderId, oldId);
        }
      }*/

      /*if (isZip(file)) {
        try {
          await unzip(selectedWallet?.provider, processedGraph, file, storageProps);
        } catch (error) {
          throw new FileUnzipError(path);
        }
      }*/
    } else {
      setStatus("selectedAccount or file is null");
    }
  };
  return (
    <>
      <MDBModal staticBackdrop tabIndex="-1" closeOnEsc={false} show={props.visible} className="file-upload-modal">
        <MDBModalDialog scrollable centered size="xl">
          <MDBModalContent>
            <MDBModalHeader>
              <div className="title-text">{t('modals.upload.title')}</div>
              <button
                type="button"
                className="button subtle medium"
                onClick={() => props.hideModal()}
                style={{ minWidth: "unset" }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8.70703 8L13.8535 2.85352C14.0488 2.6582 14.0488 2.3418 13.8535 2.14648C13.6582 1.95117 13.3418 1.95117 13.1465 2.14648L8 7.29297L2.85352 2.14648C2.6582 1.95117 2.3418 1.95117 2.14648 2.14648C1.95117 2.3418 1.95117 2.6582 2.14648 2.85352L7.29297 8L2.14648 13.1465C1.95117 13.3418 1.95117 13.6582 2.14648 13.8535C2.24414 13.9512 2.37207 14 2.5 14C2.62793 14 2.75586 13.9512 2.85352 13.8535L8 8.70703L13.1465 13.8535C13.2441 13.9512 13.3721 14 13.5 14C13.6279 14 13.7559 13.9512 13.8535 13.8535C14.0488 13.6582 14.0488 13.3418 13.8535 13.1465L8.70703 8Z"
                    fill="#ffffff"
                  />
                </svg>
              </button>
            </MDBModalHeader>
            <MDBModalBody>
              <div className="fileupload-div">
                <div className="path-select-div">
                  <div className="select-div">
                    <div className="folder-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M12.5049 13H3.49508C3.09846 12.9992 2.71833 12.8411 2.43807 12.5605C2.1578 12.2799 2.00026 11.8995 2 11.5029V4.49707C2.00088 4.09979 2.1593 3.71907 2.44049 3.43842C2.72169 3.15778 3.10272 3.00011 3.5 3H6.5C6.81011 3.00026 7.11254 3.09654 7.36574 3.27559C7.61893 3.45465 7.81049 3.7077 7.91408 4H12.5088C12.9049 4.00127 13.2843 4.15958 13.5639 4.44023C13.8435 4.72088 14.0003 5.10095 14 5.49707V11.5029C13.9997 11.8995 13.8422 12.2799 13.5619 12.5605C13.2816 12.8412 12.9015 12.9992 12.5049 13ZM3.5 4C3.36782 3.99976 3.24095 4.05195 3.14721 4.14514C3.05347 4.23833 3.00053 4.36489 3 4.49707V11.5029C2.99976 11.6345 3.05178 11.7607 3.14462 11.854C3.23746 11.9472 3.36352 11.9997 3.49508 12H12.5049C12.6359 11.9981 12.761 11.945 12.8535 11.8521C12.946 11.7593 12.9986 11.634 13 11.5029V5.49707C12.9991 5.36652 12.9473 5.24147 12.8555 5.14861C12.7637 5.05575 12.6393 5.00242 12.5088 5H7.5C7.43433 5.00004 7.36929 4.98714 7.30861 4.96203C7.24793 4.93691 7.19279 4.90008 7.14635 4.85365C7.09992 4.80721 7.06309 4.75207 7.03797 4.69139C7.01286 4.63071 6.99996 4.56567 7 4.5C7.0007 4.43415 6.98824 4.36882 6.96336 4.30784C6.93848 4.24686 6.90167 4.19146 6.8551 4.1449C6.80854 4.09833 6.75314 4.06152 6.69216 4.03664C6.63119 4.01176 6.56585 3.9993 6.5 4H3.5Z"
                          fill="#ffffff"
                        />
                      </svg>
                    </div>
                    {props.modalText === "" && !resumeBatchInfo && (
                      <MDBSelect data={folderList} onValueChange={(val) => setSelectedPath([val?.value, val?.text])} />
                    )}
                    {resumeBatchInfo && (
                      <span style={{ color: '#8b9eb0', fontSize: '14px', marginLeft: '8px' }}>
                        {t('files.upload.resumingTo') || 'Resuming to:'} {resumeBatchInfo.basePath || '/'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="file-upload-div">
                  <FileUploadComponent
                    setIsFiles={(value) => setIsFiles(value)}
                    setFiles={(files) => setFiles(files)}
                    files={files}
                    selectedPath={selectedPath}
                    modalParam={props.modalParam}
                    isResumeMode={!!resumeBatchInfo}
                  />
                </div>
                {resumeWarning && (
                  <div style={{ marginTop: "16px" }}>
                    <WarningTicket
                      close={() => setResumeWarning("")}
                      text={resumeWarning}
                    />
                  </div>
                )}
              </div>
              {isPreparing && (
                <div className="preparing-overlay">
                  <div className="preparing-content">
                    <DwinityLoader size={24} fullScreen={false} inline />
                    <span className="preparing-text">{t('files.upload.buildingStructure')}</span>
                    <span className="preparing-subtext">{files.filter(f => !f.isDelete).length} {t('common.labels.files')}</span>
                  </div>
                </div>
              )}
            </MDBModalBody>
            <MDBModalFooter>
              <div className="actions-button-div">
                <button className="button subtle medium " onClick={() => props.hideModal()} disabled={isPreparing}>
                  <span className="text">{t('common.buttons.cancel')}</span>
                </button>
                <button className="button brand medium " disabled={!isFiles || isPreparing} onClick={() => uploadFiles()}>
                  <span className="text">{isPreparing ? t('files.upload.buildingStructure') : t('common.buttons.upload')}</span>
                </button>
              </div>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
      {isVisibility5 && <DwinityLoader />}
      {isErrorTicket && (
        <>
          <ErrorTicket
            close={() => {
              setIsErrorTicket(false);
            }}
            text={errortext}
          />
          <div style={{ height: "20px" }} />
        </>
      )}
    </>
  );
}
